/**
 * Cuenta regresiva en segundos. Auditoría de reutilización 28/09: el mismo
 * `useState + setInterval que resta 1 y en 0 hace algo` estaba tres veces en el
 * Vault (contraseña revelada 30 s, código TOTP, código MFA del link público).
 *
 *   const left = useCountdown(seconds, key, onExpire);
 *
 * - `seconds` null ⇒ apagada (devuelve 0). Un número ⇒ arranca desde ahí.
 * - `key` reinicia la cuenta aunque `seconds` repita el mismo valor (p. ej. dos
 *   códigos TOTP seguidos con el mismo `expiresIn`).
 * - `onExpire` se llama UNA vez cuando la cuenta LLEGÓ a 0 desde un valor > 0.
 *   Regresión del 28/09 (hotfix): la primera versión disparaba en el mismo
 *   render en que arrancaba (el `left` viejo todavía era 0) — el TOTP quedaba
 *   congelado en "expira en 29 s" y la contraseña revelada se ocultaba sola.
 */
import { useEffect, useRef, useState } from 'react';

export function useCountdown(seconds: number | null, key: unknown, onExpire?: () => void): number {
  const [left, setLeft] = useState(seconds ?? 0);
  const cb = useRef(onExpire);
  cb.current = onExpire;
  const timer = useRef<number | null>(null);
  // "armada" recién cuando se vio un valor > 0: así el 0 anterior al arranque no cuenta como vencimiento
  const armed = useRef(false);

  useEffect(() => {
    armed.current = false;
    if (seconds === null) {
      setLeft(0);
      return;
    }
    setLeft(seconds);
    timer.current = window.setInterval(() => setLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => {
      if (timer.current !== null) window.clearInterval(timer.current);
      timer.current = null;
    };
  }, [seconds, key]);

  useEffect(() => {
    if (left > 0) {
      armed.current = true;
      return;
    }
    if (!armed.current) return;
    armed.current = false;
    if (timer.current !== null) window.clearInterval(timer.current);
    timer.current = null;
    cb.current?.();
  }, [left]);

  return left;
}
